import { faPencilAlt } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useState, useEffect } from 'react'
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom'
import { Content } from 'ui'
import { useCurrentUser } from '../../../queries'
import { ProfileData } from '../../../utils/profileHelpers'
import {
  EducatorStepOne,
  EducatorStepTwoForm as EducatorStepTwo,
  EducatorStepThree,
} from '../../SignUp/Forms'
import {
  ProfessionalStepOne,
  ProfessionalStepTwoForm as ProfessionalStepTwo,
  ProfessionalStepThree,
} from '../../SignUp/Forms'
import { User } from '../../../queries'

interface OutletContextType {
  profile: User | null
  loading: boolean
}

interface ProfileEditViewProps extends Partial<OutletContextType> {
  profileId?: string
  profile?: ProfileData | null
}

export const ProfileEditView: React.FC<ProfileEditViewProps> = (props) => {
  const outletContext = useOutletContext<OutletContextType>()
  const profile = props.profile || outletContext?.profile
  const loading = props.loading || outletContext?.loading
  const profileId = props.profileId || profile?.userId

  const navigate = useNavigate()
  const { data: currentUser } = useCurrentUser()
  const [searchParams, setSearchParams] = useSearchParams()
  const targetField = searchParams.get('targetField')

  // Determine initial tab based on targetField or tab query param
  const getInitialTab = () => {
    const tabParam = searchParams.get('tab')
    if (tabParam && ['about', 'interests', 'bio'].includes(tabParam)) {
      return tabParam
    }

    if (!targetField) return 'about'

    const fieldToTabMap: Record<string, string> = {
      // Step One / About tab fields
      about: 'about',
      jobField: 'about',
      firstName: 'about',
      lastName: 'about',
      phone: 'about',

      // Step Two / Interests tab fields
      contentDescription: 'interests',
      keywords: 'interests',
      connectionPreference: 'interests',

      // Step Three / Bio tab fields
      publicBio: 'bio',
      collaboratorDescription: 'bio',
    }

    return fieldToTabMap[targetField] || 'about'
  }

  const [currentTab, setCurrentTab] = useState(getInitialTab)
  const [updateSuccess, setUpdateSuccess] = useState(false)

  const handleTabSelect = ({ value }: Content.ITab) => {
    setCurrentTab(value)
    // Update URL query params to reflect current tab (replace history instead of pushing)
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev)
        if (targetField) {
          newParams.set('tab', value)
        } else {
          newParams.delete('targetField')
          newParams.set('tab', value)
        }
        return newParams
      },
      { replace: true }
    )
  }

  useEffect(() => {
    if (
      updateSuccess &&
      currentUser?.status === 'NEEDS_APPROVAL' &&
      currentUser?.userRole === 'PROFESSIONAL'
    ) {
      navigate('/onboard/professional/four')
      return
    }

    if (updateSuccess) {
      const timer = setTimeout(() => {
        setUpdateSuccess(false)
      }, 5000)

      return () => clearTimeout(timer)
    }
  }, [updateSuccess, currentUser, navigate])

  // Get the role consistently using the getProfileRole helper
  const role = profile ? profile.role || profile.userRole : null

  return (
    <React.Fragment>
      <header className="mb-1 mt-2 py-1 flex item-center">
        <Content.HeaderSectionTitle className="mr-2">
          <FontAwesomeIcon icon={faPencilAlt} />
        </Content.HeaderSectionTitle>
        <Content.HeaderSectionTitle>Edit Profile</Content.HeaderSectionTitle>
      </header>

      {!profile && !loading ? (
        <Content.Text className="pb-4 self-center pt-2">
          Not quite getting the matches you expected? You can revise or update
          any aspect of your profile as needed.
        </Content.Text>
      ) : null}

      {!loading && profile ? (
        <Content.Tabs
          currentValue={currentTab}
          tabs={[
            { label: 'About', value: 'about' },
            {
              label:
                profile.userRole === 'EDUCATOR' || profile.role === 'EDUCATOR'
                  ? 'Subjects & Interests'
                  : 'Support Interests',
              value: 'interests',
            },
            { label: 'Public Bio', value: 'bio' },
          ]}
          onSelectTab={handleTabSelect}
        />
      ) : null}

      {!loading && currentTab === 'about' ? (
        <div className="bg-white rounded-lg p-6 shadow mt-4 mb-32">
          {role === 'EDUCATOR' ? (
            <EducatorStepOne
              onSuccess={() => setUpdateSuccess(true)}
              hideNavigation={true}
              editMode={true}
              profileId={profileId}
              targetField={targetField || undefined}
            />
          ) : (
            <ProfessionalStepOne
              onSuccess={() => setUpdateSuccess(true)}
              hideNavigation={true}
              editMode={true}
              profileId={profileId}
              targetField={targetField || undefined}
            />
          )}
        </div>
      ) : null}

      {!loading && currentTab === 'interests' ? (
        <div className="bg-white rounded-lg p-6 shadow mt-4 mb-32">
          {role === 'EDUCATOR' ? (
            <EducatorStepTwo
              onSuccess={() => setUpdateSuccess(true)}
              hideNavigation={true}
              editMode={true}
              profileId={profileId}
              targetField={targetField || undefined}
            />
          ) : (
            <ProfessionalStepTwo
              onSuccess={() => setUpdateSuccess(true)}
              hideNavigation={true}
              editMode={true}
              profileId={profileId}
              targetField={targetField || undefined}
            />
          )}
        </div>
      ) : null}

      {!loading && currentTab === 'bio' ? (
        <div className="bg-white rounded-lg p-6 shadow mt-4 mb-32">
          {role === 'EDUCATOR' ? (
            <EducatorStepThree
              onSuccess={() => setUpdateSuccess(true)}
              hideNavigation={true}
              editMode={true}
              profileId={profileId}
              targetField={targetField || undefined}
            />
          ) : (
            <ProfessionalStepThree
              onSuccess={() => setUpdateSuccess(true)}
              hideNavigation={true}
              editMode={true}
              profileId={profileId}
              targetField={targetField || undefined}
            />
          )}
        </div>
      ) : null}
    </React.Fragment>
  )
}
